import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FormattedMessage } from "react-intl";

import Button from "@/components/Button";
import PlaceOrder from "@/components/Medicines/PlaceOrder";
import GetImages from "@/components/GetImages";

import style from "./index.module.css";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  medicine,
  setIsPrescriptionPreviewModal,
  setPlaceOrderModalOpenClosed,
} from "@/store/slices/patient/medicine";

import { pdfRegex, transformDateToMMDDYYY } from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

import { IKeyPairsValues } from "@/models/doctor";

type PrescriptionPreviewProps = {
  isOpen: boolean;
  closeHandler: () => void;
  medicalRecords: IKeyPairsValues;
  actionHandler: () => void;
  btnLabelId: string;
  headerTitleId: string;
};

const PrescriptionPreview: React.FC<PrescriptionPreviewProps> = ({
  isOpen = true,
  closeHandler,
  medicalRecords,
  actionHandler,
  btnLabelId,
  headerTitleId,
}) => {
  const { isPlaceOrderModalOpen } = useAppSelector(medicine);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const dispatch = useAppDispatch();

  const handleClose = () => {
    if (!isMobile) {
      dispatch(setIsPrescriptionPreviewModal());
    }
    dispatch(setPlaceOrderModalOpenClosed());
  };

  const isSmallScreen = useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={closeHandler}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallScreen}
        scroll={"paper"}
      >
        <DialogTitle sx={{ bgcolor: "var(--neutral-100)" }}>
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <IconButton sx={{ p: 0 }} onClick={closeHandler}>
              <GetImages name="LeftArrowIcon" width="20" height="18" />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={500}>
              <FormattedMessage id={headerTitleId} />
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {medicalRecords?.doctorName && (
            <Typography component={"h5"} textAlign={"center"} gutterBottom>
              {medicalRecords?.doctorName}
            </Typography>
          )}
          <Typography component={"p"} textAlign={"center"} gutterBottom>
            {transformDateToMMDDYYY(medicalRecords?.documentDate)}
          </Typography>
          {pdfRegex.test(medicalRecords?.url) ? (
            <Box
              sx={{
                height: { xs: "50rem", sm: "100%", md: "50rem" },
                width: "100%",
                border: "none",
                overflow: "hidden",
              }}
            >
              <iframe
                title="pdf"
                src={`${medicalRecords?.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                style={{
                  height: "100%",
                  width: "100%",
                  border: "none",
                }}
              />
            </Box>
          ) : (
            <img
              className={style.imgContainer}
              src={medicalRecords?.url}
              alt="medical record"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button btnTrigger={actionHandler}>
            <FormattedMessage defaultMessage={"btnLabelId"} id={btnLabelId} />
          </Button>
        </DialogActions>
      </Dialog>
      {isPlaceOrderModalOpen && (
        <PlaceOrder isOpen={isPlaceOrderModalOpen} closeHandler={handleClose} />
      )}
    </>
  );
};

export default PrescriptionPreview;
